import React, { useEffect, useState } from 'react';
import { 
    Card, CardContent, Typography, Box, Table, TableBody, 
    TableCell, TableContainer, TableHead, TableRow, Paper, 
    Chip, LinearProgress, CircularProgress
} from '@mui/material';
import { reportService } from '../../services/reportService';

interface PredictiveItem {
    _id: string;
    name: string;
    unit: string;
    currentStock: number;
    velocity: number;
    daysRemaining: number;
}

const InventoryAlertsWidget: React.FC = () => {
    const [data, setData] = useState<PredictiveItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const predictiveData = await reportService.getPredictiveInventory();
                setData(predictiveData.slice(0, 5)); // Show top 5 critical items
            } catch (error) {
                console.error('Failed to fetch predictive inventory', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const getStatusColor = (days: number) => {
        if (days <= 2) return 'error';
        if (days <= 5) return 'warning';
        return 'success';
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Predictive Inventory Burn Rate</Typography>
                    <Chip label="7D Velocity Model" size="small" variant="outlined" />
                </Box>
                
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: 'action.hover' }}>
                            <TableRow>
                                <TableCell>Ingredient</TableCell>
                                <TableCell align="right">Stock</TableCell>
                                <TableCell align="right">Daily Burn</TableCell>
                                <TableCell align="right">Est. Days Left</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length > 0 ? (
                                data.map((item) => (
                                    <TableRow key={item._id}>
                                        <TableCell component="th" scope="row">
                                            <Typography variant="body2" fontWeight="medium">{item.name}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {item.currentStock.toFixed(1)} {item.unit}
                                        </TableCell>
                                        <TableCell align="right">
                                            {item.velocity.toFixed(2)} / day
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip 
                                                label={item.daysRemaining > 99 ? '>99 Days' : `${item.daysRemaining.toFixed(1)} Days`}
                                                color={getStatusColor(item.daysRemaining)}
                                                size="small"
                                                sx={{ minWidth: 80 }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                        <Typography variant="body2" color="textSecondary">
                                            No sales data available for projection.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};

export default InventoryAlertsWidget;
